import { Scene } from "excalibur";
import { SPAWNING } from "../config";
import { Virus, EnemyType, SpawnCallback } from "../actors/Virus";
import { Macrophage } from "../actors/Macrophage";

/**
 * VirusPool - Object Pool for Viruses
 * 
 * Pre-allocates a fixed number of virus instances to prevent garbage collection
 * spikes during gameplay. This is critical for mobile performance.
 * 
 * Now supports multiple enemy types with different stats.
 * 
 * POOL EXHAUSTION HANDLING:
 * When the pool is full, automatically despawns the farthest virus from
 * the player to make room for new spawns. This ensures gameplay continues
 * smoothly even at maximum intensity.
 */
export class VirusPool {
    private pool: Virus[] = [];
    private scene: Scene;
    private initialized: boolean = false;

    constructor(scene: Scene) {
        this.scene = scene;
    }

    /**
     * Initialize pool - must be called after scene is ready
     */
    public init(): void {
        if (this.initialized) return;
        this.initialized = true;

        // Create viruses and add to scene
        for (let i = 0; i < SPAWNING.POOL_SIZE; i++) {
            const virus = new Virus();
            virus.deactivate();
            this.pool.push(virus);
            this.scene.add(virus);
        }
    }

    /**
     * Spawn a virus at the given position with optional type
     * If the pool is exhausted, despawns the farthest virus to make room.
     */
    public spawn(
        x: number,
        y: number,
        target: Macrophage,
        speed?: number,
        type: EnemyType = EnemyType.INFLUENZA
    ): Virus | null {
        // Find first inactive virus
        let virus: Virus | undefined = this.pool.find(v => !v.isPoolActive());

        // If pool is exhausted, recycle the farthest virus from the player
        if (!virus) {
            const recycled = this.recycleFarthestVirus(target);
            if (!recycled) {
                // This should never happen, but just in case
                console.warn('VirusPool: Unable to recycle any virus');
                return null;
            }
            virus = recycled;
        }

        // For Mutated Strain, provide spawn callback for children
        const spawnCallback: SpawnCallback | undefined =
            type === EnemyType.MUTATED_STRAIN
                ? (cx, cy) => this.spawnChild(cx, cy, target)
                : undefined;

        virus.activate(x, y, target, type, speed, spawnCallback);
        return virus;
    }

    /**
     * Find and recycle the virus farthest from the player
     * This allows new spawns to continue even when pool is at capacity
     */
    private recycleFarthestVirus(target: Macrophage): Virus | null {
        const activeViruses = this.pool.filter(v => v.isPoolActive());

        if (activeViruses.length === 0) return null;

        let farthest: Virus | null = null;
        let maxDistSq = -1;

        for (const virus of activeViruses) {
            const distSq = target.pos.squareDistance(virus.pos);
            if (distSq > maxDistSq) {
                maxDistSq = distSq;
                farthest = virus;
            }
        }

        if (farthest) {
            // Silently deactivate (no XP/antigen drops for recycled enemies)
            farthest.deactivate();
        }

        return farthest;
    }

    /**
     * Spawn a Rhinovirus child from a Mutated Strain
     */
    private spawnChild(x: number, y: number, target: Macrophage): void {
        this.spawn(x, y, target, undefined, EnemyType.RHINOVIRUS);
    }

    /**
     * Return a virus to the pool
     */
    public despawn(virus: Virus): void {
        virus.deactivate();
    }

    /**
     * Get all active viruses
     */
    public getActiveViruses(): Virus[] {
        return this.pool.filter(v => v.isPoolActive());
    }

    /**
     * Get count of active viruses
     */
    public getActiveCount(): number {
        let count = 0;
        for (const v of this.pool) {
            if (v.isPoolActive()) count++;
        }
        return count;
    }

    /**
     * Get pool capacity
     */
    public getPoolSize(): number {
        return this.pool.length;
    }

    /**
     * Get pool usage as percentage (0-100)
     */
    public getPoolUsagePercent(): number {
        return Math.round((this.getActiveCount() / this.pool.length) * 100);
    }

    /**
     * Deactivate all viruses
     */
    public clear(): void {
        for (const v of this.pool) {
            v.deactivate();
        }
    }
}

// Re-export EnemyType for convenience
export { EnemyType };

